/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  FlightRule,
  TransportOfferAlgorithm,
  VehicleType,
} from 'src/graphql/generated/graphql.generated';
import {
  RequiredCapabilityIdListRecord,
  RequiredCapabilityIdListProps,
} from 'src/store/generated/required-capability-id-list-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CommunicationCenterTransportOfferOptionProps {
  id: string;
  requestedVehicleType: VehicleType | null;
  transportOfferAlgorithm: TransportOfferAlgorithm | null;
  minimumFlightRule: FlightRule | null;
  includeCompetitors: boolean | null;
  includeAbortedUnit: boolean;
  requiredCapabilities: List<RequiredCapabilityIdListProps>;
  communicationCenterId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CommunicationCenterTransportOfferOptionRecord extends Record<CommunicationCenterTransportOfferOptionProps>(
  {
    id: null as any,
    requestedVehicleType: null as any,
    transportOfferAlgorithm: null as any,
    minimumFlightRule: null as any,
    includeCompetitors: null as any,
    includeAbortedUnit: null as any,
    requiredCapabilities: null as any,
    communicationCenterId: null as any,
    dateLastUpdated: null as any,
    dateCreated: null as any,
    dateDeleted: null as any,
    lastUpdatedById: null as any,
  },
) {
  public static fromJS(json: {
    id: string;
    requestedVehicleType: VehicleType | null;
    transportOfferAlgorithm: TransportOfferAlgorithm | null;
    minimumFlightRule: FlightRule | null;
    includeCompetitors: boolean | null;
    includeAbortedUnit: boolean;
    requiredCapabilities: any;
    communicationCenterId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CommunicationCenterTransportOfferOptionRecord {
    if (json == null) {
      throw new Error(
        'CommunicationCenterTransportOfferOptionRecord.fromJS: parameter cannot be null',
      );
    }
    return new CommunicationCenterTransportOfferOptionRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CommunicationCenterTransportOfferOptionJson.id',
      ),
      requestedVehicleType: json.requestedVehicleType,
      transportOfferAlgorithm: json.transportOfferAlgorithm,
      minimumFlightRule: json.minimumFlightRule,
      includeCompetitors: json.includeCompetitors,
      includeAbortedUnit: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'includeAbortedUnit', false),
        'CommunicationCenterTransportOfferOptionJson.includeAbortedUnit',
      ),
      requiredCapabilities: List<RequiredCapabilityIdListRecord>(
        getFromOrThrow(
          json,
          (j) => j.requiredCapabilities,
          'CommunicationCenterTransportOfferOptionJson.requiredCapabilities',
        ).map((o: any) => new RequiredCapabilityIdListRecord(o)),
      ),
      communicationCenterId: json.communicationCenterId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    requestedVehicleType: VehicleType | null;
    transportOfferAlgorithm: TransportOfferAlgorithm | null;
    minimumFlightRule: FlightRule | null;
    includeCompetitors: boolean | null;
    includeAbortedUnit: boolean;
    requiredCapabilities: any;
    communicationCenterId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CommunicationCenterTransportOfferOptionRecord, Error[]] {
    if (json == null) {
      throw new Error(
        'CommunicationCenterTransportOfferOptionRecord.fromJSUnsafe: parameter cannot be null',
      );
    }
    const errors: Error[] = [];

    const record = new CommunicationCenterTransportOfferOptionRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CommunicationCenterTransportOfferOptionJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestedVehicleType: tryOrDefault(
        () => json.requestedVehicleType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportOfferAlgorithm: tryOrDefault(
        () => json.transportOfferAlgorithm,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      minimumFlightRule: tryOrDefault(
        () => json.minimumFlightRule,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      includeCompetitors: tryOrDefault(
        () => json.includeCompetitors,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      includeAbortedUnit: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'includeAbortedUnit', false),
            'CommunicationCenterTransportOfferOptionJson.includeAbortedUnit',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requiredCapabilities: tryOrDefault(
        () =>
          List<RequiredCapabilityIdListRecord>(
            getFromOrThrow(
              json,
              (j) => j.requiredCapabilities,
              'CommunicationCenterTransportOfferOptionJson.requiredCapabilities',
            ).map((o: any) => new RequiredCapabilityIdListRecord(o)),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenterId: tryOrDefault(
        () => json.communicationCenterId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CommunicationCenterTransportOfferOptionProps) {
    super(props);
    if (props == null) {
      throw new Error(
        'CommunicationCenterTransportOfferOptionRecord constructor parameter cannot be null',
      );
    }
  }
}

export abstract class CommunicationCenterTransportOfferOptionViewModelBase
  implements CommunicationCenterTransportOfferOptionProps
{
  protected readonly _props: CommunicationCenterTransportOfferOptionProps;
  private _json?: CommunicationCenterTransportOfferOptionProps;

  get id(): string {
    return this._props.id;
  }
  get requestedVehicleType(): VehicleType | null {
    return this._props.requestedVehicleType;
  }
  get transportOfferAlgorithm(): TransportOfferAlgorithm | null {
    return this._props.transportOfferAlgorithm;
  }
  get minimumFlightRule(): FlightRule | null {
    return this._props.minimumFlightRule;
  }
  get includeCompetitors(): boolean | null {
    return this._props.includeCompetitors;
  }
  get includeAbortedUnit(): boolean {
    return this._props.includeAbortedUnit;
  }
  get requiredCapabilities(): List<RequiredCapabilityIdListProps> {
    return this._props.requiredCapabilities;
  }
  get communicationCenterId(): string | null {
    return this._props.communicationCenterId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAir() {
    return this._props.requestedVehicleType === VehicleType.Air;
  }
  public get isFixedWing() {
    return this._props.requestedVehicleType === VehicleType.FixedWing;
  }
  public get isGround() {
    return this._props.requestedVehicleType === VehicleType.Ground;
  }
  public get isRotorWing() {
    return this._props.requestedVehicleType === VehicleType.RotorWing;
  }
  public get isUnspecified() {
    return this._props.requestedVehicleType === VehicleType.Unspecified;
  }
  public get isShortestDistance() {
    return this._props.transportOfferAlgorithm === TransportOfferAlgorithm.ShortestDistance;
  }
  public get isShortestEta() {
    return this._props.transportOfferAlgorithm === TransportOfferAlgorithm.ShortestEta;
  }
  public get isIfr() {
    return this._props.minimumFlightRule === FlightRule.Ifr;
  }
  public get isNone() {
    return this._props.minimumFlightRule === FlightRule.None;
  }
  public get isVfr() {
    return this._props.minimumFlightRule === FlightRule.Vfr;
  }

  public isAnyRequestedVehicleType(...vehicleType: VehicleType[]) {
    return (
      this._props.requestedVehicleType != null &&
      vehicleType.includes(this._props.requestedVehicleType)
    );
  }
  public isAnyTransportOfferAlgorithm(...transportOfferAlgorithm: TransportOfferAlgorithm[]) {
    return (
      this._props.transportOfferAlgorithm != null &&
      transportOfferAlgorithm.includes(this._props.transportOfferAlgorithm)
    );
  }
  public isAnyMinimumFlightRule(...flightRule: FlightRule[]) {
    return (
      this._props.minimumFlightRule != null && flightRule.includes(this._props.minimumFlightRule)
    );
  }

  constructor(props: CommunicationCenterTransportOfferOptionProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        requestedVehicleType: this._props.requestedVehicleType,
        transportOfferAlgorithm: this._props.transportOfferAlgorithm,
        minimumFlightRule: this._props.minimumFlightRule,
        includeCompetitors: this._props.includeCompetitors,
        includeAbortedUnit: this._props.includeAbortedUnit,
        requiredCapabilities: this._props.requiredCapabilities,
        communicationCenterId: this._props.communicationCenterId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCommunicationCenterTransportOfferOptionEntities = (state: AppState) =>
  state.entityStores.communicationCenterTransportOfferOption.entities;

export const selectCommunicationCenterTransportOfferOptionById =
  (id: string) => (state: AppState) =>
    state.entityStores.communicationCenterTransportOfferOption.entities.get(id);

export const selectCommunicationCenterTransportOfferOptionMetadataById =
  (id: string) => (state: AppState) =>
    state.entityStores.communicationCenterTransportOfferOption.meta.get(id);

export const selectCommunicationCenterTransportOfferOptionAndMetadataById = (id: string) =>
  createSelector(
    selectCommunicationCenterTransportOfferOptionById(id),
    selectCommunicationCenterTransportOfferOptionMetadataById(id),
    (communicationCenterTransportOfferOption, meta) => ({
      communicationCenterTransportOfferOption,
      meta,
    }),
  );

export function selectCommunicationCenterTransportOfferOptionEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCommunicationCenterTransportOfferOptionEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CommunicationCenterTransportOfferOptionRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCommunicationCenterTransportOfferOptionEntitiesByRequestedVehicleType(
  requestedVehicleType: VehicleType,
) {
  return createSelector(selectAllCommunicationCenterTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.requestedVehicleType === requestedVehicleType).valueSeq(),
  );
}

export function selectCommunicationCenterTransportOfferOptionEntitiesByTransportOfferAlgorithm(
  transportOfferAlgorithm: TransportOfferAlgorithm,
) {
  return createSelector(selectAllCommunicationCenterTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.transportOfferAlgorithm === transportOfferAlgorithm).valueSeq(),
  );
}

export function selectCommunicationCenterTransportOfferOptionEntitiesByMinimumFlightRule(
  minimumFlightRule: FlightRule,
) {
  return createSelector(selectAllCommunicationCenterTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.minimumFlightRule === minimumFlightRule).valueSeq(),
  );
}

export function selectCommunicationCenterTransportOfferOptionEntitiesByIncludeCompetitors(
  includeCompetitors: boolean,
) {
  return createSelector(selectAllCommunicationCenterTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.includeCompetitors === includeCompetitors).valueSeq(),
  );
}

export function selectCommunicationCenterTransportOfferOptionEntitiesByIncludeAbortedUnit(
  includeAbortedUnit: boolean,
) {
  return createSelector(selectAllCommunicationCenterTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.includeAbortedUnit === includeAbortedUnit).valueSeq(),
  );
}

export function selectCommunicationCenterTransportOfferOptionEntitiesByCommunicationCenterId(
  communicationCenterId: string,
) {
  return createSelector(selectAllCommunicationCenterTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.communicationCenterId === communicationCenterId).valueSeq(),
  );
}
//#endregion Selectors
